/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/dashboards/ManagerDashboard.tsx
 * PURPOSE: Vietnamese F&B manager dashboard với real-time KPIs và staff management
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-03-20
 * COMPLIANCE: SDLC 6.3.1 + Sprint Governance Rule 3 (Zero Mock Tolerance)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance) + P6 (Component Library)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 7: Performance SLAs (<50ms p95)
 * SPRINT: S75-003 — Chart.js → Recharts + setTimeout removal + real TanStack hooks
 *
 * CTO FINDING #32 RESOLVED: Removed await new Promise(resolve => setTimeout(resolve, 800))
 * All hardcoded staffMembers/todayTasks/revenueData/occupancyData removed.
 * DoD: grep -rn "setTimeout|simulated|hardcoded" ManagerDashboard.tsx = 0 matches
 *
 * @business-context
 * - Domain: Business Analytics, Dashboard Visualization, Vietnamese F&B Metrics
 * - Impact: Business Intelligence, Decision Making, Performance Tracking
 * - Stakeholders: Branch Managers, Operations Team, Executive Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Data Visualization
 * - Pattern: React 18, TanStack Query v5, Recharts (replaces Chart.js)
 * - Dependencies: recharts, @tanstack/react-query, useManagerDashboard hooks
 *
 * @cross-references
 * - Hooks: frontend/src/hooks/useManagerDashboard.ts
 * - API: frontend/src/services/api/managerApi.ts
 * - Backend: backend/app/api/v2/endpoints/manager_approvals.py
 * - Design: docs/02-design/04-ui-ux-design/SPEC-0407-manager-dashboards-design.md
 * ===============================================================================
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Legend,
} from 'recharts';
import type { Formatter, ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import {
  useManagerKPIs,
  useManagerAlerts,
  useActiveStaff,
} from '../../hooks/useManagerDashboard';
import { useAuthStore } from '../../store/auth';
import { hasCapability } from '../RoleBasedRouter';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationDropdown } from '../navigation/NotificationDropdown';
import { ComplaintDashboardTiles } from '../ComplaintDashboardTiles';
import { ZoneFilter } from './ZoneFilter';
import { FnbKpiSection } from './FnbKpiSection';

// =============================================================================
// Helpers
// =============================================================================

const STATUS_COLORS: Record<string, string> = {
  clocked_in:     '#48bb78',
  overtime_start: '#764ba2',
  break_start:    '#f6ad55',
  break_end:      '#f6ad55',
  late:           '#f56565',
  absent:         '#a0aec0',
  clocked_out:    '#a0aec0',
};

const SEVERITY_COLORS: Record<string, string> = {
  high:   '#f56565',
  medium: '#f6ad55',
  low:    '#48bb78',
};

// ISS-018-H: Status labels per BR-DASH-001 §2.2 + §6.2
const STATUS_VI: Record<string, string> = {
  clocked_in:     'Đang làm',
  overtime_start: 'Tăng ca',
  break_start:    'Nghỉ giữa ca',
  break_end:      'Đang làm',
  late:           'Trễ',         // ISS-018-H: base, appended with "[X]p" dynamically
  absent:         'Chưa vào',    // ISS-018-H: BR-DASH-001 §6.2 = "Chưa vào"
  clocked_out:    'Đã về',
};

// ISS-018-C: Sort priority for Red→Yellow→Green (BR-DASH-001 §2.2)
const STATUS_SORT_ORDER: Record<string, number> = {
  absent: 0, late: 1, overtime_start: 2, clocked_in: 3,
  break_start: 4, break_end: 5, clocked_out: 6,
};

const ALERT_ICON: Record<string, string> = {
  late_arrival:     '⏰',
  absent:           '❌',
  pending_approval: '📋',
  overtime:         '⚡',
};

function formatHours(h: number): string {
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return `${hrs}g${mins > 0 ? ` ${mins}p` : ''}`;
}

// recharts Formatter type-safe cascade fix 2026-06-04 — Lane
// DEVOPS-LATENT-DEBT-CLEANUP-PR (replaces inline arrow that fails recharts
// v3 5-arg Formatter<TValue, TName> signature; per @devops root-cause
// investigation: errors latent pre-existing pre-PR #118)
// Note: Tooltip.formatter prop expects Formatter<ValueType, NameType>
// (default-generic). Typing as Formatter<number, string> fails contravariance
// — caller may pass any ValueType (string | number | array). Use ValueType
// generic + narrow at runtime via Number()/String() coercion.
const staffCountFormatter: Formatter<ValueType, NameType> = (value) =>
  [`${Number(value ?? 0)} người`, ''];
const hoursFormatter: Formatter<ValueType, NameType> = (value) =>
  [formatHours(Number(value ?? 0)), 'Giờ làm'];

// ISS-018-H: Dynamic late minutes label
function formatStatusText(status: string, lateMinutes?: number): string {
  if (status === 'late' && lateMinutes && lateMinutes > 0) {
    return `Trễ ${lateMinutes}p`;
  }
  return STATUS_VI[status] ?? status;
}

// =============================================================================
// Component
// =============================================================================

const ManagerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'overview' | 'staff' | 'operations' | 'reports'>('overview');

  // S95L-010: Pull-to-refresh state + touch handlers
  // S96L-011: touchStartY uses null sentinel (cleaner than 0) — distinguishes
  // "not started" from "started at top of page" (clientY can legitimately be 0)
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef<number | null>(null);
  const PULL_THRESHOLD = 80;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (window.scrollY === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (touchStartY.current === null || isRefreshing) return;
    const distance = e.touches[0].clientY - touchStartY.current;
    if (distance > 0 && window.scrollY === 0) {
      setPullDistance(Math.min(distance * 0.5, 120));
    }
  }, [isRefreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      setIsRefreshing(true);
      await queryClient.invalidateQueries();
      setIsRefreshing(false);
    }
    setPullDistance(0);
    touchStartY.current = null;
  }, [pullDistance, isRefreshing, queryClient]);

  // SPEC-0407-ADDENDUM §4 / TDZ fix: sync Zustand read (not a hook) must precede
  // TanStack hooks because isCorporateManagerAtL1 gates useActiveStaff enabled param.
  // useAuthStore.getState() is synchronous — no stale-read risk within a single render.
  const { userRole: _syncRole, user: _syncUser } = useAuthStore.getState();
  const isCorporateManagerAtL1 = (
    (_syncRole ?? '').toLowerCase() === 'corporate_manager' &&
    (_syncUser as any)?.organization?.level === 1
  );

  // TanStack Query hooks — NO useState/useEffect/setTimeout (S75-001/002/003)
  const { data: kpis, isLoading: kpisLoading, error: kpisError } = useManagerKPIs();
  const { data: alertsData, isLoading: alertsLoading } = useManagerAlerts({ limit: 20 });
  const { data: activeStaffData, isLoading: staffLoading } = useActiveStaff(!isCorporateManagerAtL1);

  // ISSUE-013: Notification bell — BR-LEAVE-001 §4, SPEC-0704 §7.1
  const { unreadCount, hasHighPriorityUnread } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // BUG-008 P2b: Use auth store logout() — clears correct keys (access_token, refresh_token, auth-storage)
  const authLogout = useAuthStore((s) => s.logout);
  const currentUser = useAuthStore((s) => s.user);
  const storeUserRole = useAuthStore((s) => s.userRole);
  // S95L-008: Zone filter state + branch_id from KPIs response
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const branchId = (kpis as any)?.branch_id
    ?? (currentUser as any)?.branch_id
    ?? null;
  const handleLogout = () => {
    authLogout();
    navigate('/login');
  };

  // Role guard: staff/employee users cannot access manager dashboard
  // DD-0002 §7 + Errata 2026-04-19: Manager-level roles — must match RequireRole allowedRoles in App.tsx
  // HEAD_CHEF + SHIFT_MANAGER added per BR-PADM-009 REQ-C (MANAGE capabilities = manager access)
  const userRole = storeUserRole?.toLowerCase() || (currentUser as any)?.role?.toLowerCase() || '';
  const MANAGER_LEVEL_ROLES = new Set([
    'branch_manager', 'shift_manager', 'head_chef',
    'director', 'chairman', 'tenant_owner', 'tenant_admin',
    'corporate_manager',
  ]);
  const isManagerRole = MANAGER_LEVEL_ROLES.has(userRole);

  // isCorporateManagerAtL1 declared above TanStack hooks (SPEC-0407-ADDENDUM §4 TDZ fix)
  if (currentUser && userRole && !isManagerRole) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
        <div style={{ fontSize: '48px' }}>🚫</div>
        <h2 style={{ color: '#991b1b' }}>Không có quyền truy cập</h2>
        <p style={{ color: '#6b7280' }}>Trang này chỉ dành cho quản lý.</p>
        <button onClick={() => navigate('/dashboard/staff')} style={{ padding: '10px 20px', minHeight: '44px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
          Về trang nhân viên
        </button>
      </div>
    );
  }

  // SPEC-0407-ADDENDUM §2: capability gate — kpis + alerts widgets require ANALYTICS_READ (AC-6)
  const hasAnalyticsAccess = hasCapability('ANALYTICS_READ');
  if (!hasAnalyticsAccess) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
        <div style={{ fontSize: '48px' }}>📊</div>
        <h2 style={{ color: '#374151' }}>Không có quyền xem báo cáo</h2>
        <p style={{ color: '#6b7280' }}>Tài khoản chưa được cấp quyền Analytics. Liên hệ quản trị viên.</p>
      </div>
    );
  }

  // Derived Recharts data from real KPIs
  const staffPieData = kpis
    ? [
        { name: 'Có mặt',   value: Math.max(0, kpis.present_today - kpis.late_today), color: '#48bb78' },
        { name: 'Đến muộn', value: kpis.late_today,                                   color: '#f56565' },
        { name: 'Vắng mặt', value: kpis.absent_today,                                 color: '#a0aec0' },
        { name: 'Tăng ca',  value: kpis.overtime_today,                               color: '#764ba2' },
      ].filter(d => d.value > 0)
    : [];

  const staffBarData = (activeStaffData?.active_staff ?? [])
    .slice(0, 10)
    .map(s => ({
      name:  s.full_name.split(' ').pop() ?? s.full_name,
      hours: s.hours_worked,
    }));

  const isInitialLoading = kpisLoading && alertsLoading && staffLoading;

  if (isInitialLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '20px',
      }}>
        <div style={{
          width: '60px', height: '60px',
          border: '4px solid rgba(255,255,255,0.3)',
          borderTop: '4px solid white',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <div style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>
          Đang tải dữ liệu...
        </div>
        <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (kpisError) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.95)', borderRadius: '20px',
          padding: '32px', textAlign: 'center', maxWidth: '400px',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
            Lỗi tải dữ liệu
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '24px' }}>
            Không thể kết nối đến máy chủ. Vui lòng thử lại.
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px', minHeight: '44px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none', borderRadius: '12px',
              color: 'white', fontSize: '16px', fontWeight: '600', cursor: 'pointer',
            }}
          >
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", sans-serif',
        overscrollBehaviorY: 'contain',
      }}
    >

      {/* S95L-010: Pull-to-refresh indicator */}
      {pullDistance > 0 && (
        <div style={{
          height: `${pullDistance}px`, display: 'flex', alignItems: 'center',
          justifyContent: 'center', color: 'rgba(255,255,255,0.8)', fontSize: '14px',
          transition: pullDistance === 0 ? 'height 0.2s ease' : 'none',
        }}>
          {isRefreshing ? '🔄 Đang tải...' : pullDistance >= PULL_THRESHOLD ? '↑ Thả để làm mới' : '↓ Kéo xuống để làm mới'}
        </div>
      )}

      {/* ====== HEADER ====== */}
      <div style={{
        background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(20px)',
        padding: '20px 24px', boxShadow: '0 8px 32px rgba(31,38,135,0.15)',
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', maxWidth: '1400px', margin: '0 auto',
        }}>
          <div>
            <h1 style={{
              fontSize: '28px', fontWeight: '700', margin: '0',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Bảng điều khiển quản lý
            </h1>
            <p style={{ fontSize: '14px', color: '#666', margin: '4px 0 0 0' }}>
              Dữ liệu thực tế từ hệ thống
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={() => navigate('/tasks')}
              style={{
                padding: '10px 16px', minHeight: '44px',
                background: 'rgba(245,101,101,0.1)',
                border: '1px solid rgba(245,101,101,0.3)',
                borderRadius: '12px', color: '#e53e3e',
                fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              📋 Giao việc
            </button>
            <button
              data-testid="nav-schedule"
              onClick={() => navigate('/schedule')}
              style={{
                padding: '10px 16px', minHeight: '44px',
                background: 'rgba(102,126,234,0.1)',
                border: '1px solid rgba(102,126,234,0.3)',
                borderRadius: '12px', color: '#667eea',
                fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              📅 Lịch làm việc
            </button>
            {(kpis?.pending_approvals ?? 0) > 0 && (
              <button
                onClick={() => navigate('/dashboard/manager/approvals')}
                style={{
                  padding: '10px 16px', minHeight: '44px',
                  background: 'rgba(245,101,101,0.1)',
                  border: '1px solid rgba(245,101,101,0.3)',
                  borderRadius: '12px', color: '#f56565',
                  fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
              >
                📋 {kpis?.pending_approvals} Chờ duyệt
              </button>
            )}
            <div style={{
              padding: '10px 16px', minHeight: '44px', background: 'rgba(72,187,120,0.1)',
              borderRadius: '12px', color: '#48bb78', fontWeight: '600', fontSize: '14px',
              display: 'flex', alignItems: 'center',
            }}>
              ● Trực tuyến
            </div>
            {/* ISSUE-013: Notification bell — BR-LEAVE-001 §4 */}
            <div ref={notifRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
                style={{
                  padding: '10px 14px', minHeight: '44px',
                  background: hasHighPriorityUnread ? 'rgba(239,68,68,0.1)' : 'rgba(102,126,234,0.1)',
                  border: `1px solid ${hasHighPriorityUnread ? 'rgba(239,68,68,0.3)' : 'rgba(102,126,234,0.3)'}`,
                  borderRadius: '12px', cursor: 'pointer', position: 'relative',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  fontWeight: '600', fontSize: '14px',
                  color: hasHighPriorityUnread ? '#dc2626' : '#667eea',
                }}
              >
                🔔 Thông báo
                {unreadCount > 0 && (
                  <span style={{
                    background: hasHighPriorityUnread ? '#dc2626' : '#667eea',
                    color: '#fff', borderRadius: '10px', padding: '1px 6px',
                    fontSize: '11px', fontWeight: '700', minWidth: '18px', textAlign: 'center',
                  }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            </div>
            {/* Notification popup — React Portal to document.body, escapes ALL stacking contexts */}
            {showNotifications && createPortal(
              <>
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998, background: 'rgba(0,0,0,0.15)' }}
                     onClick={() => setShowNotifications(false)} />
                <NotificationDropdown onClose={() => setShowNotifications(false)} />
              </>,
              document.body
            )}
            <button
              onClick={handleLogout}
              style={{
                padding: '10px 20px', minHeight: '44px', background: 'transparent',
                border: '2px solid rgba(102,126,234,0.3)', borderRadius: '12px',
                color: '#667eea', fontWeight: '600', fontSize: '14px', cursor: 'pointer',
              }}
            >
              Đăng xuất
            </button>
          </div>
        </div>
      </div>

      {/* ====== TABS ====== */}
      <div style={{ padding: '20px 24px 0', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          {(['overview', 'staff', 'operations', 'reports'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '12px 24px', minHeight: '44px',
                background: activeTab === tab ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.6)',
                backdropFilter: 'blur(10px)', border: 'none',
                borderRadius: '12px 12px 0 0',
                color: activeTab === tab ? '#667eea' : '#666',
                fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            >
              {tab === 'overview' ? 'Tổng quan' :
               tab === 'staff'    ? 'Nhân sự' :
               tab === 'operations' ? 'Vận hành' : 'Báo cáo'}
            </button>
          ))}
        </div>
      </div>

      {/* ====== MAIN CONTENT ====== */}
      <div style={{ padding: '0 24px 32px', maxWidth: '1400px', margin: '0 auto' }}>

        {/* ---- OVERVIEW TAB ---- */}
        {/* ISS-018: Layout matches SPEC-0407 §4.1 wireframe:
            [4 KPI Cards] → [Alerts Panel with action buttons] → [Staff Today Table]
            Then bonus features (charts, complaints) below the 3 core sections */}
        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gap: '20px' }}>

            {/* ═══ SECTION 1: 4 KPI Cards — ISS-018-A fix ═══
                Labels per BR-DASH-001 §6.2: "Đang ca" / "Có mặt" / "Trễ giờ" / "Vắng mặt"
                Each shows count + percentage */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '16px',
            }}>
              {(() => {
                const scheduled = kpis?.scheduled_today ?? kpis?.total_staff ?? 0;
                const present = kpis?.present_today ?? 0;
                const late = kpis?.late_today ?? 0;
                const absent = kpis?.absent_today ?? 0;
                const pctOf = (n: number) => scheduled > 0 ? Math.round((n / scheduled) * 100) : 0;

                // ISS-018-E: Trend deltas from API (vs yesterday)
                const dp = kpis?.delta_present ?? 0;
                const dl = kpis?.delta_late ?? 0;
                const da = kpis?.delta_absent ?? 0;

                return [
                  { label: 'Đang ca',  value: `${scheduled}/${kpis?.total_staff ?? 0}`, pct: `${scheduled > 0 && kpis?.total_staff ? Math.round((scheduled / kpis.total_staff) * 100) : 0}%`, icon: '👥', color: '#667eea', delta: 0 },
                  { label: 'Có mặt',   value: String(present), pct: `${pctOf(present)}%`, icon: '✅', color: '#48bb78', delta: dp },
                  { label: 'Trễ giờ',  value: String(late),    pct: `${pctOf(late)}%`,    icon: '⏰', color: '#f56565', delta: dl },
                  { label: 'Vắng mặt', value: String(absent),  pct: `${pctOf(absent)}%`,  icon: '❌', color: '#a0aec0', delta: da },
                ];
              })().map((kpi, i) => (
                <div key={i} style={{
                  background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
                  borderRadius: '20px', padding: '24px',
                  boxShadow: '0 8px 32px rgba(31,38,135,0.15)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>{kpi.label}</div>
                      <div style={{ fontSize: '32px', fontWeight: '700', color: '#1a1a1a' }}>{kpi.value}</div>
                    </div>
                    <div style={{
                      width: '48px', height: '48px',
                      background: `linear-gradient(135deg, ${kpi.color} 0%, ${kpi.color}dd 100%)`,
                      borderRadius: '12px', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontSize: '24px',
                    }}>
                      {kpi.icon}
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: kpi.color }}>{kpi.pct}</div>
                    {/* ISS-018-E: Trend indicator ↑↓ per SPEC-0407 FR-002 */}
                    {kpi.delta !== 0 && (
                      <span style={{
                        fontSize: '12px', fontWeight: '600',
                        color: kpi.delta > 0 ? '#48bb78' : '#f56565',
                      }}>
                        {kpi.delta > 0 ? '↑' : '↓'} {Math.abs(kpi.delta)} vs hôm qua
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Quick action badges — Chờ phê duyệt + Tăng ca (moved out of 4 KPI cards) */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {(kpis?.pending_approvals ?? 0) > 0 && (
                <button
                  onClick={() => navigate('/dashboard/manager/approvals')}
                  style={{
                    padding: '10px 20px', minHeight: '44px',
                    background: 'rgba(245,101,101,0.1)', border: '1px solid rgba(245,101,101,0.3)',
                    borderRadius: '12px', color: '#f56565',
                    fontWeight: '600', fontSize: '14px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px',
                  }}
                >
                  📋 {kpis?.pending_approvals} Chờ phê duyệt
                </button>
              )}
              {(kpis?.overtime_today ?? 0) > 0 && (
                <div style={{
                  padding: '10px 20px', minHeight: '44px',
                  background: 'rgba(118,75,162,0.1)', border: '1px solid rgba(118,75,162,0.3)',
                  borderRadius: '12px', color: '#764ba2',
                  fontWeight: '600', fontSize: '14px',
                  display: 'flex', alignItems: 'center', gap: '8px',
                }}>
                  ⚡ {kpis?.overtime_today} Tăng ca
                </div>
              )}
            </div>

            {/* ═══ SECTION 2: Alerts Panel — ISS-018-B + ISS-018-G fix ═══
                Action buttons: "Đánh dấu vắng" / "Bỏ qua" per BR-DASH-001 §2.3
                Click alert → navigate to approvals per BR-DASH-001 §2.3 */}
            {(alertsData?.alerts ?? []).length > 0 && (
              <div style={{
                background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
                borderRadius: '20px', padding: '24px',
                boxShadow: '0 8px 32px rgba(31,38,135,0.15)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#333', margin: 0 }}>
                    ⚠️ Cảnh báo ({alertsData?.total_count ?? 0})
                  </h3>
                  {(alertsData?.high_priority_count ?? 0) > 0 && (
                    <span style={{
                      padding: '4px 12px', background: 'rgba(245,101,101,0.1)',
                      borderRadius: '12px', color: '#f56565',
                      fontSize: '12px', fontWeight: '600',
                    }}>
                      {alertsData?.high_priority_count} ưu tiên cao
                    </span>
                  )}
                </div>
                <div style={{ display: 'grid', gap: '10px' }}>
                  {(alertsData?.alerts ?? []).slice(0, 8).map((alert, i) => (
                    <div
                      key={i}
                      onClick={() => {
                        // ISS-018-G: Click alert → navigate to relevant screen
                        if (alert.alert_type === 'pending_approval') {
                          navigate('/dashboard/manager/approvals');
                        } else if (alert.alert_type === 'absent' || alert.alert_type === 'late_arrival') {
                          navigate('/attendance');
                        }
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '12px 16px', minHeight: '44px',
                        background: `${SEVERITY_COLORS[alert.severity]}10`,
                        borderLeft: `3px solid ${SEVERITY_COLORS[alert.severity]}`,
                        borderRadius: '0 8px 8px 0',
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ fontSize: '18px' }}>{ALERT_ICON[alert.alert_type] ?? '⚠️'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{alert.message}</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>
                          {new Date(alert.timestamp).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      {/* ISS-018-B: Action buttons per BR-DASH-001 §2.3 */}
                      {alert.alert_type === 'absent' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/attendance?employee=${alert.employee_id}&action=mark_absent`); }}
                          style={{
                            padding: '10px 14px', minHeight: '44px',
                            background: 'rgba(245,101,101,0.15)', border: '1px solid rgba(245,101,101,0.3)',
                            borderRadius: '8px', color: '#e53e3e',
                            fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
                          }}
                        >
                          Đánh dấu vắng
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); /* dismiss alert from local state */ }}
                        style={{
                          padding: '10px 14px', minHeight: '44px',
                          background: 'rgba(160,174,192,0.15)', border: '1px solid rgba(160,174,192,0.3)',
                          borderRadius: '8px', color: '#718096',
                          fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                      >
                        Bỏ qua
                      </button>
                      <span style={{
                        padding: '2px 8px',
                        background: `${SEVERITY_COLORS[alert.severity]}20`,
                        color: SEVERITY_COLORS[alert.severity],
                        borderRadius: '8px', fontSize: '11px', fontWeight: '600',
                      }}>
                        {alert.severity === 'high' ? 'Cao' : 'TB'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ═══ SECTION 3: Staff Today Table — ISS-018-C + ISS-018-D fix ═══
                Visible on overview (not separate tab). Columns: Icon, Name, Time, Work Area, Status
                Sorted Red→Yellow→Green per BR-DASH-001 §2.2 */}
            {!isCorporateManagerAtL1 && (<div style={{
              background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
              borderRadius: '20px', padding: '24px',
              boxShadow: '0 8px 32px rgba(31,38,135,0.15)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#333', margin: 0 }}>
                  👥 Nhân viên hôm nay ({activeStaffData?.total_active ?? 0})
                </h3>
              </div>
              {staffLoading ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '24px' }}>Đang tải...</div>
              ) : (activeStaffData?.active_staff ?? []).length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '24px' }}>
                  Không có nhân viên nào đang ca
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid rgba(102,126,234,0.2)' }}>
                        {['', 'Tên nhân viên', 'Vai trò', 'Giờ vào', 'Khu vực', 'Trạng thái'].map(h => (
                          <th key={h} style={{
                            padding: '12px', textAlign: 'left',
                            fontSize: '12px', fontWeight: '600',
                            color: '#666', textTransform: 'uppercase',
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...(activeStaffData?.active_staff ?? [])]
                        .sort((a, b) => (STATUS_SORT_ORDER[a.status] ?? 9) - (STATUS_SORT_ORDER[b.status] ?? 9))
                        .map(staff => (
                        <tr key={staff.user_id} style={{ borderBottom: '1px solid rgba(102,126,234,0.1)' }}>
                          {/* Status icon dot */}
                          <td style={{ padding: '16px 8px 16px 12px', width: '24px' }}>
                            <span style={{
                              display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%',
                              background: STATUS_COLORS[staff.status] ?? '#a0aec0',
                            }} />
                          </td>
                          {/* Employee Name */}
                          <td style={{ padding: '16px 12px', fontSize: '14px', fontWeight: '600' }}>
                            {staff.full_name}
                          </td>
                          {/* ISS-018-N: Position/Role per SC-02 S2.7 */}
                          <td style={{ padding: '16px 12px', fontSize: '13px', color: '#666' }}>
                            {staff.position || '—'}
                          </td>
                          {/* Clock-In Time — show "--:--" for absent */}
                          <td style={{ padding: '16px 12px', fontSize: '14px' }}>
                            {staff.status === 'absent'
                              ? <span style={{ color: '#a0aec0' }}>--:--</span>
                              : new Date(staff.clock_in_time).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
                            }
                            {staff.status !== 'absent' && staff.status !== 'late' && ' ✓'}
                            {staff.status === 'late' && ' ⏰'}
                          </td>
                          {/* ISS-018-C: Work Area column */}
                          <td style={{ padding: '16px 12px', fontSize: '14px', color: '#666' }}>
                            {staff.work_area ?? '—'}
                          </td>
                          {/* ISS-018-H: Status text with dynamic late minutes */}
                          <td style={{ padding: '16px 12px' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '6px',
                              padding: '4px 12px', borderRadius: '12px',
                              fontSize: '12px', fontWeight: '600',
                              background: `${STATUS_COLORS[staff.status] ?? '#a0aec0'}20`,
                              color: STATUS_COLORS[staff.status] ?? '#a0aec0',
                            }}>
                              {formatStatusText(staff.status, staff.late_minutes)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>)}

            {/* S95L-008: Zone/WorkArea filter */}
            <ZoneFilter
              branchId={branchId}
              onZoneChange={setSelectedZoneId}
              selectedZoneId={selectedZoneId}
            />

            {/* S95L-013: F&B KPI Section — DD-0039 */}
            <FnbKpiSection branchId={branchId} />

            {/* ═══ BONUS SECTIONS (below core 3) — ISS-018-M: kept, below fold ═══ */}

            {/* Charts Row — Recharts */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
              gap: '20px',
            }}>
              <div style={{
                background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
                borderRadius: '20px', padding: '24px',
                boxShadow: '0 8px 32px rgba(31,38,135,0.15)',
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '20px' }}>
                  Trạng thái nhân sự hôm nay
                </h3>
                {staffPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={staffPieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="value">
                        {staffPieData.map((entry, idx) => (
                          <Cell key={`cell-${idx}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={staffCountFormatter} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '14px' }}>
                    Chưa có dữ liệu điểm danh hôm nay
                  </div>
                )}
              </div>
              <div style={{
                background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
                borderRadius: '20px', padding: '24px',
                boxShadow: '0 8px 32px rgba(31,38,135,0.15)',
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '20px' }}>
                  Giờ làm — nhân viên đang ca ({activeStaffData?.total_active ?? 0})
                </h3>
                {staffBarData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={staffBarData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis unit="g" tick={{ fontSize: 11 }} />
                      <Tooltip formatter={hoursFormatter} />
                      <Bar dataKey="hours" fill="#667eea" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '14px' }}>
                    {staffLoading ? 'Đang tải...' : 'Không có nhân viên đang ca'}
                  </div>
                )}
              </div>
            </div>

            {/* Complaint Dashboard Tiles */}
            <div style={{
              background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
              borderRadius: '20px', padding: '24px',
              boxShadow: '0 8px 32px rgba(31,38,135,0.15)',
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '20px' }}>
                Khiếu nại và SLA
              </h3>
              <ComplaintDashboardTiles
                refreshInterval={60000}
                onTileClick={(metric) => navigate('/complaints')}
              />
            </div>
          </div>
        )}

        {/* ---- STAFF TAB ---- */}
        {activeTab === 'staff' && (
          <div style={{ display: 'grid', gap: '20px' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
            }}>
              {[
                { label: 'Có mặt',         value: kpis?.present_today ?? 0,  color: '#48bb78' },
                { label: 'Trễ giờ',        value: kpis?.late_today ?? 0,     color: '#f56565' },
                { label: 'Chưa vào',       value: kpis?.absent_today ?? 0,   color: '#a0aec0' },
                { label: 'Tổng nhân viên', value: kpis?.total_staff ?? 0,    color: '#667eea' },
              ].map((s, i) => (
                <div key={i} style={{
                  background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
                  borderRadius: '20px', padding: '20px',
                  boxShadow: '0 8px 32px rgba(31,38,135,0.15)', textAlign: 'center',
                }}>
                  <div style={{ fontSize: '36px', fontWeight: '700', color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: '14px', color: '#666' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Active staff table */}
            {!isCorporateManagerAtL1 && (<div style={{
              background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
              borderRadius: '20px', padding: '24px',
              boxShadow: '0 8px 32px rgba(31,38,135,0.15)',
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '20px' }}>
                Nhân viên đang ca ({activeStaffData?.total_active ?? 0})
              </h3>
              {staffLoading ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '24px' }}>Đang tải...</div>
              ) : (activeStaffData?.active_staff ?? []).length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '24px' }}>
                  Không có nhân viên nào đang ca
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid rgba(102,126,234,0.2)' }}>
                        {['Tên', 'Vai trò', 'Trạng thái', 'Giờ vào', 'Khu vực', 'Thời gian làm'].map(h => (
                          <th key={h} style={{
                            padding: '12px', textAlign: 'left',
                            fontSize: '12px', fontWeight: '600',
                            color: '#666', textTransform: 'uppercase',
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...(activeStaffData?.active_staff ?? [])]
                        .sort((a, b) => (STATUS_SORT_ORDER[a.status] ?? 9) - (STATUS_SORT_ORDER[b.status] ?? 9))
                        .map(staff => (
                        <tr key={staff.user_id} style={{ borderBottom: '1px solid rgba(102,126,234,0.1)' }}>
                          <td style={{ padding: '16px 12px', fontSize: '14px', fontWeight: '600' }}>
                            {staff.full_name}
                          </td>
                          {/* ISS-018-N: Position/Role per SC-02 S2.7 */}
                          <td style={{ padding: '16px 12px', fontSize: '13px', color: '#666' }}>
                            {staff.position || '—'}
                          </td>
                          <td style={{ padding: '16px 12px' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '6px',
                              padding: '4px 12px', borderRadius: '12px',
                              fontSize: '12px', fontWeight: '600',
                              background: `${STATUS_COLORS[staff.status] ?? '#a0aec0'}20`,
                              color: STATUS_COLORS[staff.status] ?? '#a0aec0',
                            }}>
                              <span style={{
                                width: '8px', height: '8px', borderRadius: '50%',
                                background: STATUS_COLORS[staff.status] ?? '#a0aec0',
                              }} />
                              {formatStatusText(staff.status, staff.late_minutes)}
                            </span>
                          </td>
                          <td style={{ padding: '16px 12px', fontSize: '14px' }}>
                            {staff.status === 'absent'
                              ? <span style={{ color: '#a0aec0' }}>--:--</span>
                              : new Date(staff.clock_in_time).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
                            }
                          </td>
                          <td style={{ padding: '16px 12px', fontSize: '14px', color: '#666' }}>
                            {staff.work_area ?? '—'}
                          </td>
                          <td style={{ padding: '16px 12px', fontSize: '14px' }}>
                            {formatHours(staff.hours_worked)}
                            {staff.is_overtime && (
                              <span style={{
                                marginLeft: '8px', fontSize: '11px',
                                color: '#764ba2', fontWeight: '600',
                              }}>⚡ Tăng ca</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>)}
          </div>
        )}

        {/* ---- OPERATIONS TAB ---- */}
        {activeTab === 'operations' && (
          <div style={{ display: 'grid', gap: '20px' }}>
            <div style={{
              background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
              borderRadius: '20px', padding: '24px',
              boxShadow: '0 8px 32px rgba(31,38,135,0.15)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#333', margin: 0 }}>
                  Cảnh báo vận hành
                </h3>
                <button
                  onClick={() => navigate('/dashboard/manager/approvals')}
                  style={{
                    padding: '10px 16px', minHeight: '44px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none', borderRadius: '12px',
                    color: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                  }}
                >
                  Xem phê duyệt ({kpis?.pending_approvals ?? 0})
                </button>
              </div>
              {alertsLoading ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '24px' }}>Đang tải...</div>
              ) : (alertsData?.alerts ?? []).length === 0 ? (
                <div style={{
                  textAlign: 'center', padding: '32px',
                  color: '#48bb78', fontSize: '16px',
                }}>
                  ✅ Không có cảnh báo — mọi thứ đang diễn ra tốt!
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {(alertsData?.alerts ?? []).map((alert, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center',
                      padding: '16px',
                      background: `${SEVERITY_COLORS[alert.severity]}08`,
                      borderRadius: '12px', gap: '16px',
                    }}>
                      <div style={{
                        width: '8px', height: '40px',
                        background: SEVERITY_COLORS[alert.severity],
                        borderRadius: '4px',
                      }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '4px' }}>
                          {ALERT_ICON[alert.alert_type] ?? '⚠️'} {alert.message}
                        </div>
                        {alert.employee_name && (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            Nhân viên: {alert.employee_name}
                          </div>
                        )}
                      </div>
                      <span style={{
                        padding: '4px 12px',
                        background: `${SEVERITY_COLORS[alert.severity]}20`,
                        color: SEVERITY_COLORS[alert.severity],
                        borderRadius: '8px', fontSize: '12px', fontWeight: '600',
                      }}>
                        {alert.severity === 'high' ? 'Cao' : 'TB'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---- REPORTS TAB ---- */}
        {activeTab === 'reports' && (
          <div style={{ display: 'grid', gap: '20px' }}>
            <div style={{
              background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
              borderRadius: '20px', padding: '24px',
              boxShadow: '0 8px 32px rgba(31,38,135,0.15)',
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '20px' }}>
                Tóm tắt ngày hôm nay
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
                gap: '16px',
              }}>
                {[
                  { label: 'Tỷ lệ đúng giờ',    value: kpis ? `${Math.round(kpis.on_time_rate * 100)}%` : '—', icon: '⏱️', color: '#48bb78' },
                  { label: 'Nhân viên có mặt',   value: kpis ? `${kpis.present_today}/${kpis.total_staff}` : '—', icon: '👥', color: '#667eea' },
                  { label: 'Yêu cầu chờ duyệt', value: kpis ? String(kpis.pending_approvals) : '—', icon: '📋', color: '#764ba2' },
                  { label: 'Nhân viên tăng ca',  value: kpis ? String(kpis.overtime_today) : '—', icon: '⚡', color: '#f6ad55' },
                ].map((m, i) => (
                  <div key={i} style={{
                    padding: '20px', background: `${m.color}10`,
                    borderRadius: '12px', textAlign: 'center',
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>{m.icon}</div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: m.color, marginBottom: '4px' }}>{m.value}</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>{m.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
              borderRadius: '20px', padding: '24px',
              boxShadow: '0 8px 32px rgba(31,38,135,0.15)',
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '16px' }}>
                Hiệu suất điểm danh
              </h3>
              <div style={{ display: 'grid', gap: '16px' }}>
                {[
                  {
                    label: 'Tỷ lệ đúng giờ',
                    pct: Math.round((kpis?.on_time_rate ?? 0) * 100),
                    color: '#667eea',
                  },
                  {
                    label: 'Tỷ lệ có mặt',
                    pct: kpis && kpis.total_staff > 0
                      ? Math.round((kpis.present_today / kpis.total_staff) * 100)
                      : 0,
                    color: '#48bb78',
                  },
                ].map((bar, i) => (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '13px', color: '#666' }}>{bar.label}</span>
                      <span style={{ fontSize: '13px', fontWeight: '600' }}>{bar.pct}%</span>
                    </div>
                    <div style={{
                      height: '8px', background: `${bar.color}20`,
                      borderRadius: '4px', overflow: 'hidden',
                    }}>
                      <div style={{
                        width: `${bar.pct}%`, height: '100%',
                        background: `linear-gradient(90deg, ${bar.color} 0%, ${bar.color}cc 100%)`,
                        transition: 'width 0.6s ease',
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ManagerDashboard;
